import React, { useEffect, useState } from 'react';
import {
  Check, Loader2, MessageSquare, Save, Send, TestTube2,
} from 'lucide-react';
import client from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

// Sempico: doğrulama kodunun ikinci SMS sağlayıcısı.
// Ayarlar `settings` kaydında değil `provider_configs`'tedir (bkz.
// backend/services/sempico.py), bu yüzden sayfanın ortak Kaydet düğmesine
// bağlanmaz: kendi ucu ve kendi düğmesi vardır.
export const SEMPICO_DEFAULT_TEMPLATE = '{site} doğrulama kodunuz: {code}';
const MAX_TEMPLATE = 160;

// Türkçe karakter mesajı UCS-2'ye çevirir: tek parça 70 karakterdir.
export function smsPartInfo(text) {
  const value = String(text || '');
  const unicode = /[^\x20-\x7E\n]/.test(value);
  const single = unicode ? 70 : 160;
  const multi = unicode ? 67 : 153;
  const parts = value.length <= single ? 1 : Math.ceil(value.length / multi);
  return { length: value.length, parts, unicode };
}

export function previewMessage(template, siteName) {
  return String(template || SEMPICO_DEFAULT_TEMPLATE)
    .replace('{site}', siteName || '')
    .replace('{code}', '123456')
    .replace(/\s+/g, ' ')
    .replace(/^[\s:-]+/, '');
}

const EMPTY_FORM = {
  enabled: false,
  role: 'backup',
  sender_id: '',
  message_template: SEMPICO_DEFAULT_TEMPLATE,
  username: '',
  api_token: '',
  password: '',
};

function formFrom(data) {
  return {
    enabled: Boolean(data.enabled),
    role: data.role || 'backup',
    sender_id: data.sender_id || '',
    message_template: data.message_template || SEMPICO_DEFAULT_TEMPLATE,
    username: data.username || '',
    api_token: '',
    password: '',
  };
}

export default function SempicoSettings({ canWrite, siteName, heraclesConfigured }) {
  const { toast } = useToast();
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    client.get('/admin/sempico/config')
      .then(({ data }) => {
        if (!alive) return;
        setSaved(data);
        setForm(formFrom(data));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!saved) return null;

  const set = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setStatus(null);
  };
  const templateError = !form.message_template.includes('{code}')
    ? 'Şablon {code} yer tutucusunu içermelidir.'
    : form.message_template.length > MAX_TEMPLATE
      ? `Şablon en fazla ${MAX_TEMPLATE} karakter olabilir.`
      : '';
  const preview = previewMessage(form.message_template, siteName);
  const partInfo = smsPartInfo(preview);
  const dirty = (
    form.enabled !== Boolean(saved.enabled)
    || form.role !== saved.role
    || form.sender_id !== (saved.sender_id || '')
    || form.message_template !== (saved.message_template || '')
    || form.username !== (saved.username || '')
    || Boolean(form.api_token)
    || Boolean(form.password)
  );
  const unsavedSecret = Boolean(form.api_token || form.password);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        enabled: form.enabled,
        role: form.role,
        sender_id: form.sender_id,
        message_template: form.message_template,
        username: form.username,
      };
      if (form.api_token) body.api_token = form.api_token;
      if (form.password) body.password = form.password;
      const { data } = await client.patch('/admin/sempico/config', body);
      setSaved(data);
      setForm(formFrom(data));
      toast({ title: 'Sempico ayarları kaydedildi' });
    } catch (e) {
      toast({
        title: 'Sempico ayarları kaydedilemedi',
        description: e.response?.data?.detail?.[0]?.msg
          || e.response?.data?.detail || 'Lütfen tekrar deneyin.',
      });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setStatus(null);
    try {
      const { data } = await client.post('/admin/sempico/test');
      setStatus({ ok: true, data });
    } catch (e) {
      setStatus({
        ok: false,
        message: e.response?.data?.detail || 'Sempico bağlantısı kurulamadı',
      });
    } finally {
      setTesting(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await client.post('/admin/sempico/test-sms', { phone: testPhone });
      toast({
        title: 'Test SMS’i gönderildi',
        description: 'Telefona örnek kod (123456) gelmelidir.',
      });
    } catch (e) {
      toast({
        title: 'Test SMS’i gönderilemedi',
        description: e.response?.data?.detail || 'Lütfen tekrar deneyin.',
      });
    } finally {
      setSending(false);
    }
  };

  const hasCredentials = saved.api_token_configured
    || (saved.username && saved.password_configured);
  const canProbe = hasCredentials && saved.sender_id && !dirty;

  return (
    <div
      className="col-span-full rounded-xl p-4"
      style={{
        background: 'var(--v2-hover, rgba(255,255,255,0.025))',
        border: '1px solid var(--v2-line, rgba(255,255,255,0.08))',
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <MessageSquare size={18} className="mt-0.5 shrink-0 text-cyan-300" />
          <div>
            <div className="text-sm font-semibold text-slate-100">
              Sempico — ikinci SMS sağlayıcısı
            </div>
            <p className="mt-1 text-xs leading-5 text-slate-400">
              HeraclesOtp’nin yanında çalışır. Biri kodu gönderemezse
              (kontör bitti, servis yanıt vermiyor) diğeri dener; üye
              hesabından kilitlenmez. Yalnızca Sempico tanımlıysa bütün
              SMS’ler ondan gider.
            </p>
          </div>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
            saved.ready ? 'text-emerald-300' : 'text-slate-400'
          }`}
          style={{
            background: saved.ready
              ? 'rgba(34,197,94,0.1)' : 'rgba(148,163,184,0.1)',
          }}
        >
          {saved.ready ? 'Devrede' : 'Kapalı'}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="col-span-full flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.enabled}
            disabled={!canWrite}
            onChange={(e) => set({ enabled: e.target.checked })}
          />
          <span className="font-semibold text-slate-100">Sempico’yu kullan</span>
        </label>

        <div>
          <label className="settings-field-label" htmlFor="sempico-role">
            Sıra
          </label>
          <select
            id="sempico-role"
            className="admin-input"
            value={form.role}
            disabled={!canWrite}
            onChange={(e) => set({ role: e.target.value })}
          >
            <option value="backup">Yedek — önce HeraclesOtp denenir</option>
            <option value="primary">Birincil — önce Sempico denenir</option>
          </select>
          {!heraclesConfigured && (
            <p className="mt-1 text-xs text-slate-400">
              HeraclesOtp anahtarı tanımlı değil; SMS’ler yalnızca Sempico’dan gider.
            </p>
          )}
        </div>

        <div>
          <label className="settings-field-label" htmlFor="sempico-sender">
            Gönderici adı (Sender ID)
          </label>
          <input
            id="sempico-sender"
            className="admin-input"
            value={form.sender_id}
            disabled={!canWrite}
            maxLength={32}
            onChange={(e) => set({ sender_id: e.target.value })}
            placeholder="Sempico’da onaylı ad"
          />
          <p className="mt-1 text-xs text-slate-400">
            Sempico panelinde onaylanmış olmalıdır; onaysız ad gönderimi
            reddettirir.
          </p>
        </div>

        <div className="col-span-full">
          <label className="settings-field-label" htmlFor="sempico-token">
            {`API token${saved.api_token_configured ? ' (kayıtlı)' : ''}`}
          </label>
          <input
            id="sempico-token"
            type="password"
            autoComplete="new-password"
            className="admin-input"
            value={form.api_token}
            disabled={!canWrite}
            onChange={(e) => set({ api_token: e.target.value })}
            placeholder={saved.api_token_configured
              ? 'Değiştirmek için yeni token girin'
              : 'Sempico web panelinde üretilen token'}
          />
          <p className="mt-1 text-xs text-slate-400">
            Token girildiğinde gönderim GET API ile yapılır. Token yerine
            aşağıdaki kullanıcı adı/şifre ile REST API de kullanılabilir.
          </p>
        </div>

        <div>
          <label className="settings-field-label" htmlFor="sempico-user">
            REST kullanıcı adı (isteğe bağlı)
          </label>
          <input
            id="sempico-user"
            className="admin-input"
            autoComplete="off"
            value={form.username}
            disabled={!canWrite}
            onChange={(e) => set({ username: e.target.value })}
          />
        </div>
        <div>
          <label className="settings-field-label" htmlFor="sempico-pass">
            {`REST şifresi${saved.password_configured ? ' (kayıtlı)' : ''}`}
          </label>
          <input
            id="sempico-pass"
            type="password"
            autoComplete="new-password"
            className="admin-input"
            value={form.password}
            disabled={!canWrite}
            onChange={(e) => set({ password: e.target.value })}
            placeholder={saved.password_configured
              ? 'Değiştirmek için yeni şifre girin' : ''}
          />
        </div>

        <div className="col-span-full">
          <label className="settings-field-label" htmlFor="sempico-template">
            Mesaj şablonu
          </label>
          <input
            id="sempico-template"
            className="admin-input"
            value={form.message_template}
            disabled={!canWrite}
            maxLength={MAX_TEMPLATE + 40}
            onChange={(e) => set({ message_template: e.target.value })}
          />
          <p className="mt-1 text-xs text-slate-400">
            {'{code}'} doğrulama koduyla, {'{site}'} site adıyla değişir.
            Önizleme: <span className="text-slate-200">{preview}</span>
            {' '}({partInfo.length} karakter, {partInfo.parts} SMS
            {partInfo.unicode ? ', Türkçe karakter var' : ''})
          </p>
          {templateError && (
            <p className="mt-1 text-xs text-red-400">{templateError}</p>
          )}
        </div>

        <div className="col-span-full flex flex-wrap gap-2">
          {canWrite && (
            <button
              type="button"
              className="admin-btn-primary flex items-center gap-2"
              onClick={save}
              disabled={!dirty || saving || Boolean(templateError)}
            >
              {saving
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Save className="h-4 w-4" />}
              Kaydet
            </button>
          )}
          <button
            type="button"
            className="admin-btn-secondary flex items-center gap-2"
            onClick={test}
            disabled={testing || !canProbe}
            title={canProbe
              ? 'SMS göndermeden token, gönderici adı ve rotayı dener'
              : 'Önce kimlik bilgilerini ve gönderici adını kaydedin'}
          >
            {testing
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <TestTube2 className="h-4 w-4" />}
            Bağlantıyı Kontrol Et
          </button>
        </div>
        {unsavedSecret && (
          <p className="col-span-full text-xs text-amber-300">
            Yeni kimlik bilgisi kaydedilmedi; bağlantı testi kayıtlı değerle yapılır.
          </p>
        )}

        {status && (
          <div
            className="col-span-full rounded-xl p-3 text-sm"
            role="status"
            style={{
              background: status.ok
                ? 'rgba(34,197,94,0.08)' : 'rgba(248,113,113,0.08)',
              border: status.ok
                ? '1px solid rgba(34,197,94,0.24)'
                : '1px solid rgba(248,113,113,0.24)',
            }}
          >
            <div className={`flex items-center gap-2 font-semibold ${
              status.ok ? 'text-emerald-300' : 'text-red-300'
            }`}
            >
              {status.ok && <Check size={16} />}
              {status.ok ? 'Sempico bağlantısı çalışıyor' : 'Sempico bağlantısı kurulamadı'}
            </div>
            <div className="mt-1 text-xs text-slate-400">
              {!status.ok && status.message}
              {status.ok && status.data?.preview_unavailable
                && 'Token kabul edildi; Sempico mesaj önizlemesini döndüremedi. Gönderici adını aşağıdaki test SMS’iyle doğrulayın.'}
              {status.ok && status.data?.mode === 'token' && !status.data?.preview_unavailable
                && `Token ve gönderici adı kabul edildi; örnek kod ${status.data.parts || 1} SMS parçası tutuyor.`}
              {status.ok && status.data?.mode === 'account'
                && `Hesaba giriş yapıldı. Bakiye: ${status.data.balance || '—'} ${status.data.currency || ''}`}
            </div>
          </div>
        )}

        {canWrite && (
          <div className="col-span-full">
            <label className="settings-field-label" htmlFor="sempico-test-phone">
              Test SMS’i gönder (ücretlidir)
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="sempico-test-phone"
                className="admin-input admin-input-w-md"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="05xx xxx xx xx"
              />
              <button
                type="button"
                className="admin-btn-secondary flex items-center gap-2"
                onClick={sendTest}
                disabled={sending || !testPhone.trim() || !canProbe}
              >
                {sending
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Send className="h-4 w-4" />}
                Gönder
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-400">
              Bağlantı testi SMS göndermez; gönderici adının operatörde
              gerçekten kabul edildiğini yalnızca gerçek bir SMS gösterir.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
